import { effect, inject, Injector } from '@angular/core';

/**
 * Waits for a signal to become truthy, returning its value synchronously if already truthy
 * or as a Promise that resolves when the signal becomes truthy.
 *
 * @param signalFn - A function that reads one or more signals and returns a value
 * @param injector - Optional injector; required when calling after an `await` (outside injection context)
 */
export function waitForSignal<T>(signalFn: () => T, injector?: Injector): NonNullable<T> | Promise<NonNullable<T>> {
  const value = signalFn();
  if (value) return value as NonNullable<T>;

  const inj = injector ?? inject(Injector);
  return new Promise<NonNullable<T>>((resolve) => {
    const cleanup = effect(
      () => {
        const v = signalFn();
        if (v) {
          cleanup.destroy();
          resolve(v as NonNullable<T>);
        }
      },
      { injector: inj },
    );
  });
}
